import {
  Box,
  Flex,
  HStack,
  Tag,
  TagCloseButton,
  TagLabel,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import classNames from 'classnames';
import type React from 'react';
import { useLocation } from 'react-router-dom';

import { useCurrencyPreference } from '../../../../shared/user-preferences/hooks/useCurrencyPreference';
import { shouldShowCurrentPreviousToggle } from '../config/currentPreviousConfig';
import { useFilterContext } from '../contexts/FilterProvider';
import {
  type Filter,
  FilterId,
  FilterType,
  isMultiFilterInstance,
  isSegmentedFilter,
} from '../types/filters';
import { isMaxEffectivelySet } from '../utils/common';
import { FilterPopover } from './FilterPopover';
import type { FilterTagProps } from './FilterTag';
import styles from './filter-chip.module.css';

const getSegmentsText = (
  segmentations: Record<string, Filter> | undefined
): string => {
  if (!segmentations || Object.keys(segmentations).length === 0) {
    return 'All Employees';
  }

  const allSelections: string[] = [];

  Object.values(segmentations).forEach((segmentFilter) => {
    if (segmentFilter.type === FilterType.TREE) {
      const treeFilter = segmentFilter;
      treeFilter.selections.forEach((selection) => {
        if (selection.name) {
          allSelections.push(selection.name);
        }
      });
    } else if (segmentFilter.type === FilterType.LIST) {
      const listFilter = segmentFilter;
      listFilter.selections.forEach((selection) => {
        if (selection.name) {
          allSelections.push(selection.name);
        }
      });
    }
  });

  if (allSelections.length === 0) return 'All Employees';
  if (allSelections.length > 2) {
    const first2Segments = allSelections.slice(0, 2).join(', ');
    const remainingSegmentsCount = allSelections.length - 2;
    const otherText = remainingSegmentsCount === 1 ? 'other' : 'others';

    return `${first2Segments}, and ${remainingSegmentsCount} ${otherText}`;
  }

  return allSelections.join(', ');
};

const getPrefix = (filterId: FilterId, currencySymbol: string): string => {
  const currencyFilters = [
    FilterId.SALARY,
    FilterId.LAST_FUNDING_AMOUNT,
    FilterId.TOTAL_FUNDING_AMOUNT,
  ];
  return currencyFilters.includes(filterId) ? currencySymbol : '';
};

export const SubFilterTag: React.FC<FilterTagProps> = ({
  filter,
  onRemoveFilter,
  disabled = false,
  hideDisabledTooltip = false,
}) => {
  const { exchangeRate, currencySymbol } = useCurrencyPreference();
  const { dispatch } = useFilterContext();
  const location = useLocation();

  const filterToRender = isSegmentedFilter(filter)
    ? filter.wrappedFilter
    : filter;

  const handleSubfilterClear = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (isMultiFilterInstance(filter)) {
      dispatch({
        type: 'REMOVE_MULTI_FILTER_INSTANCE',
        payload: {
          parentId: filter.parentMultiFilterId,
          instanceIndex: filter.instanceIndex,
        },
      });
      return;
    }
    onRemoveFilter(filter.id);
  };

  const segmentsText = isSegmentedFilter(filter)
    ? getSegmentsText(filter.segmentations)
    : 'All Employees';

  const values = (() => {
    if (filterToRender.type !== FilterType.RANGE) {
      return null;
    }

    // Range filters should always have min and max properties (even if undefined)
    // but after URL deserialization they might be missing, so we handle that gracefully
    const min = 'min' in filterToRender ? filterToRender.min : undefined;
    const max = 'max' in filterToRender ? filterToRender.max : undefined;

    const isRateFilter =
      filterToRender.id === FilterId.ATTRITION_RATE ||
      filterToRender.id === FilterId.HIRING_RATE ||
      filterToRender.id === FilterId.GROWTH_RATE;
    const isCurrencyFilter =
      filterToRender.id === FilterId.SALARY ||
      filterToRender.id === FilterId.LAST_FUNDING_AMOUNT ||
      filterToRender.id === FilterId.TOTAL_FUNDING_AMOUNT;

    const startValue =
      isCurrencyFilter && min !== undefined ? min * exchangeRate : min;
    const endValue = (() => {
      if (max === undefined) return undefined;
      return isCurrencyFilter ? max * exchangeRate : max;
    })();

    const prefix = getPrefix(filterToRender.id, currencySymbol);
    const suffix = isRateFilter ? '%' : '';
    const formattedStartValue = `${prefix}${startValue}${suffix}`;
    const formattedEndValue = `${prefix}${endValue}${suffix}`;

    const isNumber = (value: number | undefined): value is number =>
      typeof value === 'number';

    const isMinSet = min !== undefined;
    const maxEffectivelySet = isMaxEffectivelySet(
      max,
      filterToRender.id,
      isMinSet
    );

    // Only show "from X to Y" if both values exist AND max is effectively set (not a default)
    if (isNumber(startValue) && isNumber(endValue) && maxEffectivelySet) {
      return (
        <HStack spacing={1} display="inline-flex">
          <Text as="span" fontSize="xs">
            from
          </Text>
          <Text as="span" fontSize="xs" className={styles.value}>
            {formattedStartValue}
          </Text>
          <Text as="span" fontSize="xs">
            to
          </Text>
          <Text as="span" fontSize="xs" className={styles.value}>
            {formattedEndValue}
          </Text>
        </HStack>
      );
    }

    // If only startValue exists (greater than case), show "is greater than"
    if (isNumber(startValue)) {
      return (
        <HStack spacing={1} display="inline-flex">
          <Text as="span" fontSize="xs">
            is greater than
          </Text>
          <Text as="span" fontSize="xs" className={styles.value}>
            {formattedStartValue}
          </Text>
        </HStack>
      );
    }

    if (isNumber(endValue)) {
      return (
        <HStack spacing={1} display="inline-flex">
          <Text as="span" fontSize="xs">
            is less than
          </Text>
          <Text as="span" fontSize="xs" className={styles.value}>
            {formattedEndValue}
          </Text>
        </HStack>
      );
    }

    return null;
  })();

  if (filterToRender.type === FilterType.RANGE && !values) {
    return null;
  }

  const filterChipName = (() => {
    const shouldShowCurrentPrevious = shouldShowCurrentPreviousToggle(
      filter.id,
      location.pathname
    );

    const name = filter.name;

    if (!shouldShowCurrentPrevious || !('isCurrent' in filter)) {
      return name;
    }

    const prefix = filter.isCurrent ? 'Current' : 'Previous';
    return `${prefix} ${name}`;
  })();

  const renderTag = ({ isDisabled }: { isDisabled: boolean }) => {
    return (
      <Tag
        size="sm"
        variant="solid"
        className={classNames(
          styles.chip,
          styles.bgSubtle,
          isDisabled && styles.disabled
        )}
        data-testid={`filter-chip-${filter.id}`}
        pointerEvents="auto"
      >
        <Flex alignItems="center" minH="24px">
          <TagLabel>
            <HStack spacing={1}>
              <Text as="span" className={styles.title}>
                {filterChipName}
              </Text>
              <HStack spacing={1}>
                <Text as="span" fontSize="xs">
                  • for{' '}
                </Text>
                <Text as="span" className={styles.value} fontSize="xs">
                  {segmentsText}
                </Text>
                <Text as="span" fontSize="xs">
                  •
                </Text>
              </HStack>
              {values}
            </HStack>
          </TagLabel>
          <TagCloseButton
            onClick={handleSubfilterClear}
            data-testid="clear-filter-chip"
            ml={1}
            pointerEvents="auto"
          />
        </Flex>
      </Tag>
    );
  };

  const renderTrigger = (() => {
    if (disabled) {
      const tooltipProps = {
        label: 'This filter is unavailable on this page.',
        variant: 'label',
        hasArrow: true,
        openDelay: 375,
        isDisabled: hideDisabledTooltip,
      };
      return (
        <Tooltip {...tooltipProps}>{renderTag({ isDisabled: true })}</Tooltip>
      );
    }

    return renderTag({ isDisabled: false });
  })();

  return (
    values !== null && (
      <FilterPopover
        filter={filter}
        trigger={<Box display="inline-block">{renderTrigger}</Box>}
      />
    )
  );
};
